/* eslint-disable */
import React from "react";
import { supabase } from "./supabaseClient";

const C = {
  primary:"#1a6b3c", primaryPale:"#e8f5ee", primaryMid:"#6ee7b7",
  amber:"#d97706",   amberPale:"#fffbeb",   amberMid:"#fde68a",
  rose:"#e11d48",    rosePale:"#fef2f2",
  blue:"#2563eb",    bluePale:"#eff6ff",
  teal:"#0d9488",    tealPale:"#f0fdfa",
  text:"#111827",    muted:"#6b7280",       border:"#d1fae5",
  white:"#ffffff",   bg:"#f9fafb",          shadow:"rgba(0,0,0,0.06)",
};

const STATUS_META = {
  open:         { label:"Unassigned",   bg:"#fef2f2", fg:"#dc2626", bd:"#fecaca", icon:"⭕" },
  assigned:     { label:"Assigned",     bg:"#fffbeb", fg:"#d97706", bd:"#fde68a", icon:"📌" },
  acknowledged: { label:"Acknowledged", bg:"#eff6ff", fg:"#2563eb", bd:"#bfdbfe", icon:"👁" },
  done:         { label:"Done",         bg:"#f0fdf4", fg:"#16a34a", bd:"#bbf7d0", icon:"✅" },
  not_done:     { label:"Not Done",     bg:"#fef2f2", fg:"#dc2626", bd:"#fecaca", icon:"❌" },
};

const DAYS = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

const fmt = d => d ? new Date(d).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}) : "—";

// All Mon–Fri dates between two ISO date strings
function workingDates(from, to) {
  const out = [];
  const cur = new Date(from + "T00:00:00");
  const end = new Date(to   + "T00:00:00");
  while (cur <= end) {
    if (cur.getDay() !== 0 && cur.getDay() !== 6) out.push(cur.toISOString().split("T")[0]);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

// ── CoverAssignmentModal ──────────────────────────────────────────────────────
// Opens right after a leave is approved. Shows timetable periods for the leave
// date range and lets admin assign a cover teacher to each one.
export function CoverAssignmentModal({ leaveRequest, staff = [], onClose, onSaved }) {
  const [timetable,   setTimetable]   = React.useState([]);
  const [dutyCounts,  setDutyCounts]  = React.useState({}); // staffId → total duties ever
  const [onLeaveIds,  setOnLeaveIds]  = React.useState([]); // staffIds on approved leave in this range
  const [assignments, setAssignments] = React.useState({}); // "date||pno" → {cover_staff_id, cover_staff_name}
  const [extras,      setExtras]      = React.useState({}); // "date" → [{class_name,subject,period_time}]
  const [loading,     setLoading]     = React.useState(true);
  const [saving,      setSaving]      = React.useState(false);
  const [toast,       setToast]       = React.useState(null);

  const showToast = (msg, ok=true) => { setToast({msg,ok}); setTimeout(()=>setToast(null),3500); };
  const dates     = workingDates(leaveRequest.from_date, leaveRequest.to_date);
  const absName   = leaveRequest.staff_name || "";

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      const [tt, dc, leavesInRange, existing] = await Promise.all([
        supabase.from("timetable").select("*").order("period_no"),
        supabase.from("cover_duties").select("cover_staff_id").not("cover_staff_id","is",null),
        // Staff who already have approved leave overlapping this date range
        supabase.from("staff_leave_requests")
          .select("staff_id")
          .eq("status","approved")
          .lte("from_date", leaveRequest.to_date)
          .gte("to_date",   leaveRequest.from_date),
        // Pre-fill if this leave was already partially assigned
        supabase.from("cover_duties")
          .select("*")
          .eq("leave_request_id", leaveRequest.id),
      ]);

      setTimetable(tt.data || []);

      const cnt = {};
      (dc.data||[]).forEach(r => { cnt[r.cover_staff_id] = (cnt[r.cover_staff_id]||0)+1; });
      setDutyCounts(cnt);

      setOnLeaveIds([...new Set((leavesInRange.data||[]).map(r=>r.staff_id))]);

      // Pre-fill existing assignments (edit / re-open mode)
      if (existing.data?.length) {
        const map = {};
        existing.data.forEach(d => {
          if (!d.is_manual) {
            map[`${d.date}||${d.period_no}`] = {
              cover_staff_id:   d.cover_staff_id,
              cover_staff_name: d.cover_staff_name,
            };
          }
        });
        setAssignments(map);
      }

      setLoading(false);
    })();
  }, [leaveRequest.id]);

  // Timetable periods for a specific date, matching the absent staff's name
  const periodsFor = (date) => {
    const dayName = DAYS[new Date(date + "T00:00:00").getDay()];
    return timetable
      .filter(t => t.day === dayName &&
        (t.teacher_name||"").trim().toLowerCase() === absName.trim().toLowerCase())
      .sort((a,b) => (a.period_no||0)-(b.period_no||0));
  };

  const setAssign = (key, s) =>
    setAssignments(p => ({...p, [key]: s ? {cover_staff_id:s.id, cover_staff_name:s.name} : {cover_staff_id:null,cover_staff_name:null}}));

  const addExtra = (date) =>
    setExtras(p => ({...p, [date]: [...(p[date]||[]), {class_name:"",subject:"",period_time:""}]}));

  const setExtraField = (date, idx, field, val) =>
    setExtras(p => { const a=[...(p[date]||[])]; a[idx]={...a[idx],[field]:val}; return {...p,[date]:a}; });

  const removeExtra = (date, idx) =>
    setExtras(p => ({...p,[date]:(p[date]||[]).filter((_,i)=>i!==idx)}));

  const availFor = (date) =>
    staff.filter(s => s.id !== leaveRequest.staff_id && s.status !== "Inactive" && !onLeaveIds.includes(s.id));

  const whatsappMsg = (date, period, coverName) => {
    const ds = new Date(date+"T00:00:00").toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long"});
    const cls  = period.class  || period.class_name || "";
    const subj = period.subject || "";
    const time = period.start_time || period.period_time || "";
    return `Assalamu Alaikum ${coverName},\n\nYou are requested to cover *${cls} – ${subj}*${time?" ("+time+")":""} on *${ds}* in place of *${absName}*.\n\nPlease confirm. JazakAllah Khair.`;
  };

  const save = async () => {
    setSaving(true);
    const rows = [];

    dates.forEach(date => {
      const periods = periodsFor(date);
      const ex      = (extras[date]||[]);

      // Timetable-based periods
      periods.forEach(p => {
        const key   = `${date}||${p.period_no}`;
        const asgn  = assignments[key]||{};
        rows.push({
          leave_request_id:  leaveRequest.id,
          absent_staff_id:   leaveRequest.staff_id,
          absent_staff_name: leaveRequest.staff_name,
          date,
          period_no:         p.period_no,
          period_time:       p.start_time ? `${p.start_time}${p.end_time?" – "+p.end_time:""}` : null,
          class_name:        p.class || null,
          subject:           p.subject || null,
          cover_staff_id:    asgn.cover_staff_id    || null,
          cover_staff_name:  asgn.cover_staff_name  || null,
          status:            asgn.cover_staff_id ? "assigned" : "open",
          is_manual:         false,
        });
      });

      // Manual (extra) periods — use high period_no (200+idx) to avoid clashing
      ex.forEach((e,idx) => {
        const pno  = 200 + idx;
        const key  = `${date}||extra_${idx}`;
        const asgn = assignments[key]||{};
        rows.push({
          leave_request_id:  leaveRequest.id,
          absent_staff_id:   leaveRequest.staff_id,
          absent_staff_name: leaveRequest.staff_name,
          date,
          period_no:         pno,
          period_time:       e.period_time || null,
          class_name:        e.class_name  || null,
          subject:           e.subject     || null,
          cover_staff_id:    asgn.cover_staff_id    || null,
          cover_staff_name:  asgn.cover_staff_name  || null,
          status:            asgn.cover_staff_id ? "assigned" : "open",
          is_manual:         true,
        });
      });
    });

    if (!rows.length) { onSaved(); return; }

    const { error } = await supabase.from("cover_duties").upsert(rows, {
      onConflict: "absent_staff_id,date,period_no",
      ignoreDuplicates: false,
    });
    setSaving(false);
    if (error) { showToast("Error: " + error.message, false); return; }

    // Notify each assigned cover teacher (one notification per person)
    const coverGroups = {};
    rows.filter(r => r.cover_staff_id).forEach(r => {
      if (!coverGroups[r.cover_staff_id]) {
        coverGroups[r.cover_staff_id] = { name: r.cover_staff_name, dates: new Set(), classes: new Set() };
      }
      coverGroups[r.cover_staff_id].dates.add(r.date);
      if (r.class_name) coverGroups[r.cover_staff_id].classes.add(r.class_name);
    });
    const notifRows = Object.entries(coverGroups).map(([staffId, info]) => {
      const staffMember = (staff||[]).find(s => s.id === staffId);
      const dateList  = [...info.dates].sort().join(", ");
      const classList = [...info.classes].join(", ") || "assigned classes";
      return {
        title: "🔄 Cover Duty Assigned",
        body: `Cover ${classList} on ${dateList} for ${leaveRequest.staff_name}.`,
        type: "alert",
        target_role: "teachers",
        target_class: staffMember?.email || null,
        source_type: "cover_duty",
        created_by: "Admin",
      };
    });
    if (notifRows.length) supabase.from("notifications").insert(notifRows);

    const open = rows.filter(r=>!r.cover_staff_id).length;
    showToast(open > 0
      ? `Saved! ${open} period${open!==1?"s":""} still unassigned — assign later in Cover Duty tab.`
      : "All cover duties assigned and saved!");
    setTimeout(() => onSaved(), 1400);
  };

  // Totals for progress bar
  const totalPeriods = dates.reduce((acc,d) => acc + periodsFor(d).length + (extras[d]||[]).length, 0);
  const assignedCount = Object.values(assignments).filter(a=>a?.cover_staff_id).length;
  const openCount = Math.max(0, totalPeriods - assignedCount);

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.55)",zIndex:9999,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"24px 16px",overflowY:"auto"}}>
      <div style={{background:C.white,borderRadius:18,width:"100%",maxWidth:700,boxShadow:"0 24px 64px rgba(0,0,0,.28)",marginBottom:32}}>

        {/* ── Header ── */}
        <div style={{background:`linear-gradient(135deg,${C.primary},#2d9c5e)`,borderRadius:"18px 18px 0 0",padding:"22px 26px",color:C.white}}>
          <div style={{fontWeight:900,fontSize:19,marginBottom:6}}>📋 Assign Cover Duties</div>
          <div style={{fontSize:13,opacity:.88,lineHeight:1.5}}>
            <strong>{absName}</strong> is on leave from <strong>{fmt(leaveRequest.from_date)}</strong> to <strong>{fmt(leaveRequest.to_date)}</strong>
            <br/>{dates.length} working day{dates.length!==1?"s":""} need cover assigned
          </div>
          {toast && (
            <div style={{marginTop:12,background:toast.ok?"rgba(255,255,255,.15)":"rgba(220,38,38,.2)",borderRadius:8,padding:"8px 14px",fontSize:13,fontWeight:700}}>
              {toast.ok?"✅":"⚠️"} {toast.msg}
            </div>
          )}
        </div>

        {/* ── Progress strip ── */}
        <div style={{background:"#e5e7eb",height:5}}>
          <div style={{background:openCount===0?C.primary:C.amber,height:"100%",transition:"width .4s",
            width:`${totalPeriods>0?Math.round((assignedCount/totalPeriods)*100):0}%`}}/>
        </div>
        <div style={{padding:"8px 26px",background:"#f9fafb",borderBottom:`1px solid ${C.border}`,display:"flex",gap:18,fontSize:12,flexWrap:"wrap"}}>
          <span style={{color:C.primary,fontWeight:700}}>✅ {assignedCount} assigned</span>
          <span style={{color:openCount>0?C.amber:"#9ca3af",fontWeight:700}}>⭕ {openCount} open</span>
          <span style={{color:C.muted}}>📚 {totalPeriods} total periods across {dates.length} days</span>
        </div>

        {/* ── Body ── */}
        <div style={{padding:"20px 26px",display:"flex",flexDirection:"column",gap:18,maxHeight:"58vh",overflowY:"auto"}}>
          {loading && (
            <div style={{color:C.muted,fontSize:13,textAlign:"center",padding:"32px 0"}}>
              <div style={{display:"inline-block",width:22,height:22,border:`3px solid ${C.border}`,borderTopColor:C.primary,borderRadius:"50%",animation:"spin .8s linear infinite",marginBottom:8}}/>
              <div>Loading timetable…</div>
            </div>
          )}

          {!loading && dates.map(date => {
            const periods = periodsFor(date);
            const ex      = extras[date] || [];
            const dayName = DAYS[new Date(date+"T00:00:00").getDay()];
            const avail   = availFor(date);
            const dateLabel = new Date(date+"T00:00:00").toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"short"});

            return (
              <div key={date} style={{border:`1px solid ${C.border}`,borderRadius:13,overflow:"hidden"}}>
                {/* Day header */}
                <div style={{background:C.primaryPale,padding:"10px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div style={{fontWeight:800,fontSize:13,color:C.primary}}>{dateLabel}</div>
                  <div style={{fontSize:11,color:C.muted}}>
                    {periods.length > 0
                      ? `${periods.length} period${periods.length!==1?"s":""} in timetable`
                      : "no timetable entries"}
                  </div>
                </div>

                <div style={{padding:"12px 16px",display:"flex",flexDirection:"column",gap:10}}>

                  {/* No periods notice */}
                  {periods.length === 0 && ex.length === 0 && (
                    <div style={{fontSize:12,color:C.muted,fontStyle:"italic",padding:"6px 0"}}>
                      No timetable periods found for <strong>{absName}</strong> on {dayName}.
                      <span style={{color:C.amber,marginLeft:6}}>
                        Tip: check that the timetable stores the teacher name exactly as: "{absName}"
                      </span>
                    </div>
                  )}

                  {/* Timetable-derived periods */}
                  {periods.map(p => {
                    const key  = `${date}||${p.period_no}`;
                    const asgn = assignments[key] || {};
                    const assigned = !!asgn.cover_staff_id;
                    return (
                      <div key={key}
                        style={{background:assigned?"#f0fdf4":"#fffbeb",borderRadius:10,padding:"11px 14px",
                          border:`1px solid ${assigned?"#bbf7d0":"#fde68a"}`,transition:"all .2s"}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,flexWrap:"wrap"}}>
                          <div style={{flex:1,minWidth:160}}>
                            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                              <span style={{background:C.primaryPale,color:C.primary,borderRadius:6,padding:"1px 8px",fontSize:11,fontWeight:800}}>
                                P{p.period_no}
                              </span>
                              {(p.start_time||p.end_time) && (
                                <span style={{fontSize:11,color:C.muted}}>
                                  {p.start_time}{p.end_time?" – "+p.end_time:""}
                                </span>
                              )}
                            </div>
                            <div style={{fontWeight:800,fontSize:13,color:C.text}}>
                              {p.class} · {p.subject}
                            </div>
                          </div>
                          <div style={{display:"flex",gap:7,alignItems:"center",flexWrap:"wrap",flexShrink:0}}>
                            <select
                              value={asgn.cover_staff_id||""}
                              onChange={e => {
                                const s = avail.find(x=>x.id===e.target.value);
                                setAssign(key, s||null);
                              }}
                              style={{padding:"5px 10px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:12,background:C.white,minWidth:170}}>
                              <option value="">— Unassigned —</option>
                              {avail.map(s => (
                                <option key={s.id} value={s.id}>
                                  {s.name}{dutyCounts[s.id]?` (${dutyCounts[s.id]} duties)`:""}
                                </option>
                              ))}
                            </select>
                            {assigned && (
                              <button
                                onClick={()=>{ navigator.clipboard.writeText(whatsappMsg(date,p,asgn.cover_staff_name)); showToast("Message copied! Paste into WhatsApp."); }}
                                title="Copy WhatsApp message"
                                style={{background:"#dcfce7",color:"#15803d",border:"1px solid #bbf7d0",borderRadius:7,padding:"5px 9px",fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                                📱 Copy Msg
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Manual / extra periods */}
                  {ex.map((e,idx) => {
                    const key  = `${date}||extra_${idx}`;
                    const asgn = assignments[key] || {};
                    return (
                      <div key={idx} style={{background:"#f8fafc",borderRadius:10,padding:"12px 14px",border:"1px dashed #cbd5e1"}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                          <span style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:.5}}>Manual Period</span>
                          <button onClick={()=>removeExtra(date,idx)}
                            style={{background:"#fef2f2",color:"#dc2626",border:"1px solid #fecaca",borderRadius:6,padding:"2px 9px",fontSize:12,cursor:"pointer"}}>
                            ✕ Remove
                          </button>
                        </div>
                        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:8}}>
                          <input placeholder="Class (e.g. Class 5A)" value={e.class_name}
                            onChange={ev=>setExtraField(date,idx,"class_name",ev.target.value)}
                            style={{padding:"5px 9px",borderRadius:7,border:"1px solid #d1d5db",fontSize:12,flex:1,minWidth:100}}/>
                          <input placeholder="Subject" value={e.subject}
                            onChange={ev=>setExtraField(date,idx,"subject",ev.target.value)}
                            style={{padding:"5px 9px",borderRadius:7,border:"1px solid #d1d5db",fontSize:12,flex:1,minWidth:100}}/>
                          <input placeholder="Time (e.g. 9:00 – 9:45)" value={e.period_time}
                            onChange={ev=>setExtraField(date,idx,"period_time",ev.target.value)}
                            style={{padding:"5px 9px",borderRadius:7,border:"1px solid #d1d5db",fontSize:12,flex:1,minWidth:130}}/>
                        </div>
                        <select
                          value={asgn.cover_staff_id||""}
                          onChange={ev => { const s=avail.find(x=>x.id===ev.target.value); setAssign(key,s||null); }}
                          style={{padding:"5px 10px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:12,background:C.white,width:"100%"}}>
                          <option value="">— Unassigned —</option>
                          {avail.map(s=>(
                            <option key={s.id} value={s.id}>
                              {s.name}{dutyCounts[s.id]?` (${dutyCounts[s.id]} duties)`:""}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })}

                  {/* Add manual period button */}
                  <button onClick={()=>addExtra(date)}
                    style={{background:C.bg,color:C.muted,border:"1px dashed #d1d5db",borderRadius:8,padding:"8px 14px",fontSize:12,cursor:"pointer",fontWeight:600,textAlign:"left",transition:"all .15s"}}
                    onMouseEnter={ev=>{ev.currentTarget.style.background=C.primaryPale;ev.currentTarget.style.color=C.primary;}}
                    onMouseLeave={ev=>{ev.currentTarget.style.background=C.bg;ev.currentTarget.style.color=C.muted;}}>
                    + Add manual period for this day
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Footer ── */}
        <div style={{padding:"16px 26px",borderTop:`1px solid ${C.border}`,display:"flex",gap:10,justifyContent:"flex-end",alignItems:"center",flexWrap:"wrap",background:"#fafafa",borderRadius:"0 0 18px 18px"}}>
          {openCount > 0 && (
            <div style={{fontSize:12,color:C.amber,fontWeight:700,flex:1,minWidth:180}}>
              ⚠️ {openCount} period{openCount!==1?"s":""} unassigned — saved as "Open", assign later from Cover Duty tab
            </div>
          )}
          <button onClick={onClose}
            style={{background:C.white,color:C.muted,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 20px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
            Skip for Now
          </button>
          <button onClick={save} disabled={saving||loading}
            style={{background:saving||loading?"#9ca3af":C.primary,color:C.white,border:"none",borderRadius:10,padding:"10px 26px",fontWeight:800,fontSize:13,cursor:saving||loading?"not-allowed":"pointer",boxShadow:`0 4px 16px ${C.primary}40`}}>
            {saving ? "💾 Saving…" : "💾 Save Cover Assignments"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ── DutyCard ─────────────────────────────────────────────────────────────────
// Defined outside CoverDutyAdmin so hooks inside it are stable
function DutyCard({ d, staff, onStatusChange, onReassign, showToast }) {
  const [reassignOpen, setReassignOpen] = React.useState(false);
  const st = STATUS_META[d.status] || STATUS_META.open;

  const dateLabel = d.date
    ? new Date(d.date+"T00:00:00").toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short"})
    : "—";

  const whatsappMsg = () => {
    const ds = d.date ? new Date(d.date+"T00:00:00").toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long"}) : "";
    return `Assalamu Alaikum ${d.cover_staff_name||""},\n\nReminder: you are covering *${d.class_name||"?"} – ${d.subject||"?"}*${d.period_time?" ("+d.period_time+")":""} on *${ds}* for *${d.absent_staff_name}*.\n\nPlease confirm. JazakAllah Khair.`;
  };

  return (
    <div style={{background:C.white,borderRadius:12,padding:"12px 16px",border:`1px solid ${st.bd}`,borderLeft:`4px solid ${st.fg}`,transition:"box-shadow .2s"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,flexWrap:"wrap"}}>
        <div style={{flex:1,minWidth:200}}>
          {/* Status + date */}
          <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:5,flexWrap:"wrap"}}>
            <span style={{background:st.bg,color:st.fg,border:`1px solid ${st.bd}`,borderRadius:6,padding:"1px 9px",fontSize:11,fontWeight:700}}>
              {st.icon} {st.label}
            </span>
            <span style={{fontSize:11,color:C.muted}}>
              {dateLabel}
              {d.period_no!=null&&d.period_no<200&&` · Period ${d.period_no}`}
              {d.period_time&&` · ${d.period_time}`}
            </span>
          </div>
          {/* Class + subject */}
          <div style={{fontWeight:800,fontSize:13,color:C.text}}>{d.class_name||"?"} · {d.subject||"?"}</div>
          {/* Absent / cover */}
          <div style={{fontSize:12,color:C.muted,marginTop:3}}>
            Absent: <strong>{d.absent_staff_name}</strong>
            {d.cover_staff_name
              ? <> · Cover: <strong style={{color:C.primary}}>{d.cover_staff_name}</strong></>
              : <span style={{color:"#dc2626",fontWeight:700}}> · No cover assigned</span>}
          </div>
          {d.is_manual && <span style={{fontSize:10,color:C.muted,background:"#f3f4f6",borderRadius:4,padding:"1px 6px",marginTop:4,display:"inline-block"}}>manual</span>}
        </div>

        {/* Action buttons */}
        <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center",flexShrink:0}}>
          {d.status==="assigned" && (
            <>
              <button onClick={()=>onStatusChange(d.id,"acknowledged")}
                style={{background:"#eff6ff",color:"#2563eb",border:"1px solid #bfdbfe",borderRadius:7,padding:"5px 11px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                👁 Acknowledged
              </button>
              {d.cover_staff_name && (
                <button onClick={()=>{ navigator.clipboard.writeText(whatsappMsg()); showToast("Message copied!"); }}
                  style={{background:"#dcfce7",color:"#15803d",border:"1px solid #bbf7d0",borderRadius:7,padding:"5px 9px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                  📱
                </button>
              )}
            </>
          )}
          {d.status==="acknowledged" && (
            <>
              <button onClick={()=>onStatusChange(d.id,"done")}
                style={{background:"#f0fdf4",color:"#16a34a",border:"1px solid #bbf7d0",borderRadius:7,padding:"5px 11px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                ✅ Done
              </button>
              <button onClick={()=>onStatusChange(d.id,"not_done")}
                style={{background:"#fef2f2",color:"#dc2626",border:"1px solid #fecaca",borderRadius:7,padding:"5px 11px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                ❌ Not Done
              </button>
            </>
          )}
          {(d.status==="open"||d.status==="assigned") && (
            <button onClick={()=>setReassignOpen(v=>!v)}
              style={{background:C.bg,color:C.muted,border:`1px solid ${C.border}`,borderRadius:7,padding:"5px 11px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
              {reassignOpen?"▲":"✏️ Reassign"}
            </button>
          )}
        </div>
      </div>

      {/* Inline reassign panel */}
      {reassignOpen && (
        <div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${C.border}`,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          <span style={{fontSize:12,color:C.muted,fontWeight:600}}>Assign cover:</span>
          <select defaultValue=""
            onChange={e => {
              if (e.target.value === "__clear__") { onReassign(d.id,null,null); setReassignOpen(false); return; }
              const s = staff.find(x=>x.id===e.target.value);
              if (s) { onReassign(d.id,s.id,s.name); setReassignOpen(false); }
            }}
            style={{padding:"5px 10px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:12,background:C.white,flex:1,minWidth:180}}>
            <option value="" disabled>Select staff member…</option>
            <option value="__clear__">— Remove assignment (mark as Open) —</option>
            {staff.filter(s=>s.id!==d.absent_staff_id&&s.status!=="Inactive").map(s=>(
              <option key={s.id} value={s.id}>{s.name} — {s.role}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}


// ── CoverDutyAdmin ────────────────────────────────────────────────────────────
export function CoverDutyAdmin({ staff = [] }) {
  const [duties,     setDuties]     = React.useState([]);
  const [loading,    setLoading]    = React.useState(true);
  const [tab,        setTab]        = React.useState("active");
  const [toast,      setToast]      = React.useState(null);
  const [filterDate, setFilterDate] = React.useState("");
  const [filterStaff,setFilterStaff]= React.useState("all");

  const showToast = (msg,ok=true) => { setToast({msg,ok}); setTimeout(()=>setToast(null),3000); };

  const load = React.useCallback(async()=>{
    setLoading(true);
    const {data} = await supabase.from("cover_duties").select("*").order("date",{ascending:false}).order("period_no",{ascending:true});
    setDuties(data||[]);
    setLoading(false);
  },[]);
  React.useEffect(()=>{load();},[load]);

  const updateStatus = async (id, status) => {
    const extra = status==="acknowledged" ? {acknowledged_at:new Date().toISOString()}
                : status==="done"         ? {done_at:new Date().toISOString()} : {};
    const {error} = await supabase.from("cover_duties").update({status,...extra}).eq("id",id);
    if (error) { showToast("Error: "+error.message,false); return; }
    setDuties(p=>p.map(d=>d.id===id?{...d,status,...extra}:d));
    showToast("Status updated");
  };

  const reassign = async (id, staffId, staffName) => {
    const {error} = await supabase.from("cover_duties").update({
      cover_staff_id:   staffId||null,
      cover_staff_name: staffName||null,
      status:           staffId?"assigned":"open",
    }).eq("id",id);
    if (error) { showToast("Error: "+error.message,false); return; }
    setDuties(p=>p.map(d=>d.id===id?{...d,cover_staff_id:staffId,cover_staff_name:staffName,status:staffId?"assigned":"open"}:d));
    showToast(staffId?"Reassigned successfully":"Assignment cleared");
  };

  const today        = new Date().toISOString().split("T")[0];
  const activeDuties = duties.filter(d=>["open","assigned","acknowledged"].includes(d.status));
  const doneDuties   = duties.filter(d=>["done","not_done"].includes(d.status));
  const todayOpen    = duties.filter(d=>d.date===today&&["open","assigned"].includes(d.status)).length;
  const openTotal    = duties.filter(d=>d.status==="open").length;

  // Fairness stats: duty count per cover staff
  const statsMap = {};
  duties.filter(d=>d.cover_staff_id).forEach(d=>{
    const id=d.cover_staff_id;
    if(!statsMap[id])statsMap[id]={name:d.cover_staff_name||"",total:0,done:0,pending:0};
    statsMap[id].total++;
    if(d.status==="done") statsMap[id].done++;
    else statsMap[id].pending++;
  });

  // History filters
  const filteredHistory = doneDuties.filter(d=>{
    if(filterDate && d.date!==filterDate) return false;
    if(filterStaff!=="all" && d.cover_staff_id!==filterStaff) return false;
    return true;
  });

  const tabBtn = (id,label,badge) => (
    <button onClick={()=>setTab(id)}
      style={{background:tab===id?C.primary:C.white,color:tab===id?C.white:C.text,border:`1px solid ${tab===id?C.primary:C.border}`,borderRadius:9,padding:"7px 16px",fontWeight:700,fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
      {label}
      {badge!=null&&badge!==''&&<span style={{background:tab===id?"rgba(255,255,255,.25)":C.primaryPale,color:tab===id?C.white:C.primary,borderRadius:20,padding:"0 7px",fontSize:11,fontWeight:800}}>{badge}</span>}
    </button>
  );

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      {/* Global toast */}
      {toast&&<div style={{position:"fixed",top:20,right:20,zIndex:9999,background:toast.ok?"#f0fdf4":"#fef2f2",color:toast.ok?"#16a34a":"#dc2626",border:`1px solid ${toast.ok?"#bbf7d0":"#fecaca"}`,borderRadius:10,padding:"10px 18px",fontWeight:700,fontSize:13,boxShadow:"0 4px 18px rgba(0,0,0,.15)"}}>
        {toast.msg}
      </div>}

      {/* Header */}
      <div style={{background:C.white,borderRadius:14,padding:"18px 22px",border:`1px solid ${C.border}`,boxShadow:`0 2px 10px ${C.shadow}`}}>
        <div style={{fontWeight:900,fontSize:17,color:C.primary,marginBottom:4}}>🔄 Cover Duty Management</div>
        <div style={{fontSize:12,color:C.muted,marginBottom:14}}>Assign and track cover teachers for absent staff's classes</div>

        {/* Stats strip */}
        <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:14}}>
          {[
            {icon:"⭕",label:"Unassigned",val:openTotal,col:"#dc2626",bg:"#fef2f2"},
            {icon:"📌",label:"Active Today",val:todayOpen,col:C.amber,bg:C.amberPale},
            {icon:"📋",label:"All Active",val:activeDuties.length,col:C.blue,bg:C.bluePale},
            {icon:"✅",label:"Done",val:duties.filter(d=>d.status==="done").length,col:"#16a34a",bg:"#f0fdf4"},
          ].map(({icon,label,val,col,bg})=>(
            <div key={label} style={{background:bg,borderRadius:10,padding:"8px 14px",display:"flex",alignItems:"center",gap:9,border:`1px solid ${col}20`}}>
              <span style={{fontSize:18}}>{icon}</span>
              <div>
                <div style={{fontWeight:900,fontSize:20,color:col,lineHeight:1}}>{val}</div>
                <div style={{fontSize:10,color:C.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:.3}}>{label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Tab buttons */}
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {tabBtn("active","📋 Active Duties",activeDuties.length)}
          {tabBtn("history","✅ History",doneDuties.length)}
          {tabBtn("stats","📊 Fairness Tracker","")}
        </div>
      </div>

      {/* Active Duties */}
      {tab==="active"&&(
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {loading&&<div style={{color:C.muted,fontSize:13,padding:16}}>Loading…</div>}
          {!loading&&openTotal>0&&(
            <div style={{background:"#fef2f2",borderRadius:10,padding:"10px 16px",fontSize:13,color:"#dc2626",fontWeight:700,border:"1px solid #fecaca"}}>
              ⭕ {openTotal} period{openTotal!==1?"s":""} have no cover teacher assigned yet — assign from the cards below or from the leave request.
            </div>
          )}
          {!loading&&activeDuties.length===0&&(
            <div style={{background:C.white,borderRadius:12,padding:"30px",textAlign:"center",color:C.muted,fontSize:13,border:`1px solid ${C.border}`}}>
              🎉 No active cover duties. All periods are covered or complete!
            </div>
          )}
          {!loading&&activeDuties.map(d=>(
            <DutyCard key={d.id} d={d} staff={staff} onStatusChange={updateStatus} onReassign={reassign} showToast={showToast}/>
          ))}
        </div>
      )}

      {/* History */}
      {tab==="history"&&(
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {/* Filters */}
          <div style={{background:C.white,borderRadius:10,padding:"12px 16px",border:`1px solid ${C.border}`,display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
            <span style={{fontSize:12,fontWeight:700,color:C.muted}}>Filter:</span>
            <input type="date" value={filterDate} onChange={e=>setFilterDate(e.target.value)}
              style={{padding:"5px 10px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:12}}/>
            <select value={filterStaff} onChange={e=>setFilterStaff(e.target.value)}
              style={{padding:"5px 10px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:12,background:C.white}}>
              <option value="all">All Cover Staff</option>
              {Object.entries(statsMap).map(([id,s])=><option key={id} value={id}>{s.name}</option>)}
            </select>
            {(filterDate||filterStaff!=="all")&&(
              <button onClick={()=>{setFilterDate("");setFilterStaff("all");}}
                style={{fontSize:12,color:C.muted,background:"none",border:"none",cursor:"pointer"}}>✕ Clear filters</button>
            )}
            <span style={{fontSize:12,color:C.muted,marginLeft:"auto"}}>{filteredHistory.length} records</span>
          </div>

          {filteredHistory.length===0&&<div style={{color:C.muted,fontSize:13,textAlign:"center",padding:24}}>No completed duties match the filter.</div>}
          {filteredHistory.map(d=>(
            <DutyCard key={d.id} d={d} staff={staff} onStatusChange={updateStatus} onReassign={reassign} showToast={showToast}/>
          ))}
        </div>
      )}

      {/* Fairness Stats */}
      {tab==="stats"&&(
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div style={{background:C.white,borderRadius:14,padding:"18px 22px",border:`1px solid ${C.border}`}}>
            <div style={{fontWeight:700,fontSize:14,color:C.primary,marginBottom:4}}>📊 Cover Duty Fairness Tracker</div>
            <div style={{fontSize:12,color:C.muted,marginBottom:16}}>How many cover duties each staff member has done. Use this to keep it fair when assigning.</div>
            {Object.keys(statsMap).length===0&&(
              <div style={{color:C.muted,fontSize:13}}>No cover duties assigned yet. Duties appear here as you assign them.</div>
            )}
            {Object.entries(statsMap)
              .sort((a,b)=>b[1].total-a[1].total)
              .map(([id,s],i)=>(
                <div key={id} style={{display:"flex",alignItems:"center",gap:14,padding:"11px 0",borderTop:i>0?"1px solid #f3f4f6":"none"}}>
                  {/* Rank */}
                  <div style={{width:30,height:30,borderRadius:8,background:i===0?"#fef9c3":C.primaryPale,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900,fontSize:14,color:i===0?C.amber:C.primary,flexShrink:0}}>
                    {i+1}
                  </div>
                  {/* Name + bar */}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
                      <span style={{fontWeight:700,fontSize:13,color:C.text}}>{s.name}</span>
                      <span style={{fontWeight:900,fontSize:16,color:C.primary}}>{s.total}</span>
                    </div>
                    <div style={{background:"#f3f4f6",borderRadius:4,height:8,overflow:"hidden"}}>
                      <div style={{background:C.primary,height:"100%",borderRadius:4,
                        width:`${Math.max(...Object.values(statsMap).map(x=>x.total))>0?(s.total/Math.max(...Object.values(statsMap).map(x=>x.total)))*100:0}%`,
                        transition:"width .5s"}}/>
                    </div>
                    <div style={{display:"flex",gap:12,marginTop:4}}>
                      <span style={{fontSize:11,color:"#16a34a",fontWeight:600}}>✅ {s.done} done</span>
                      <span style={{fontSize:11,color:C.amber,fontWeight:600}}>📌 {s.pending} pending</span>
                    </div>
                  </div>
                </div>
              ))
            }
          </div>

          {/* Today's plan */}
          {todayOpen>0&&(
            <div style={{background:"#fffbeb",borderRadius:12,padding:"14px 18px",border:"1px solid #fde68a",fontSize:13,color:C.amber,fontWeight:700}}>
              ⚠️ {todayOpen} cover assignment{todayOpen!==1?"s":""} for today are still unconfirmed. Remind the cover teachers!
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Export duty count for badge on tab button
export async function getCoverDutyOpenCount() {
  const {count} = await supabase.from("cover_duties").select("id",{count:"exact",head:true}).in("status",["open","assigned"]);
  return count||0;
}
